import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  ArrowLeft,
  Clock,
  CheckCircle2,
  XCircle,
  Target,
  TrendingUp,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Home,
  RefreshCw,
} from "lucide-react";

const ResultsPage = () => {
  const navigate = useNavigate();
  const { attemptId } = useParams();
  const { user, token, isAuthenticated } = useAuth();

  const [attempt, setAttempt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedQuestions, setExpandedQuestions] = useState({});

  useEffect(() => {
    fetchAttempt();
  }, [attemptId]);

  const fetchAttempt = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/practice/attempt/${attemptId}`, {
        headers,
        withCredentials: true,
      });
      setAttempt(response.data);
    } catch (error) {
      console.error("Error fetching attempt:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const toggleQuestion = (questionId) => {
    setExpandedQuestions((prev) => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!attempt) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <XCircle className="w-12 h-12 text-rose-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Results Not Found</h2>
            <p className="text-slate-600 mb-6">The practice attempt could not be found.</p>
            <Button onClick={() => navigate("/practice")}>Back to Practice</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { score, total, accuracy, time_taken, results } = attempt;
  const avgTimePerQuestion = total > 0 ? Math.round(time_taken / total) : 0;

  const getScoreColor = () => {
    if (accuracy >= 80) return "text-emerald-600";
    if (accuracy >= 60) return "text-amber-600";
    return "text-rose-600";
  };

  const getScoreBg = () => {
    if (accuracy >= 80) return "bg-emerald-50 border-emerald-200";
    if (accuracy >= 60) return "bg-amber-50 border-amber-200";
    return "bg-rose-50 border-rose-200";
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button variant="ghost" onClick={() => navigate("/practice")} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Practice
            </Button>
            <h1 className="text-lg font-semibold text-slate-900">Practice Results</h1>
            <div className="w-24"></div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Score Card */}
        <Card className={`mb-8 ${getScoreBg()} animate-fade-in`} data-testid="score-card">
          <CardContent className="p-8">
            <div className="grid md:grid-cols-4 gap-6">
              <div className="md:col-span-2 text-center md:text-left">
                <p className="text-sm font-medium text-slate-600 mb-2">Your Score</p>
                <div className="flex items-baseline gap-2 justify-center md:justify-start">
                  <span className={`text-6xl font-bold ${getScoreColor()}`}>{score}</span>
                  <span className="text-2xl text-slate-500">/ {total}</span>
                </div>
                <div className="mt-4">
                  <Progress value={accuracy} className="h-3" />
                  <p className={`text-lg font-semibold mt-2 ${getScoreColor()}`}>{accuracy.toFixed(1)}% Accuracy</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-white/60 rounded-lg">
                  <Clock className="w-5 h-5 text-slate-600" />
                  <div>
                    <p className="text-xs text-slate-500">Time Taken</p>
                    <p className="font-semibold text-slate-900">{formatTime(time_taken)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white/60 rounded-lg">
                  <Target className="w-5 h-5 text-slate-600" />
                  <div>
                    <p className="text-xs text-slate-500">Avg. per Question</p>
                    <p className="font-semibold text-slate-900">{avgTimePerQuestion}s</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-white/60 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  <div>
                    <p className="text-xs text-slate-500">Correct</p>
                    <p className="font-semibold text-emerald-600">{score} questions</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white/60 rounded-lg">
                  <XCircle className="w-5 h-5 text-rose-600" />
                  <div>
                    <p className="text-xs text-slate-500">Incorrect</p>
                    <p className="font-semibold text-rose-600">{total - score} questions</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex flex-wrap gap-4 mb-8">
          <Button onClick={() => navigate("/practice")} data-testid="practice-more-btn">
            <BookOpen className="w-4 h-4 mr-2" />
            Practice More
          </Button>
          <Button variant="outline" onClick={() => navigate(`/practice/${attempt.passage_id}`)} data-testid="retry-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry This Passage
          </Button>
          {isAuthenticated && (
            <Button variant="outline" onClick={() => navigate("/dashboard")} data-testid="dashboard-btn">
              <TrendingUp className="w-4 h-4 mr-2" />
              View Dashboard
            </Button>
          )}
        </div>

        {/* Dashboard CTA for Guests */}
        {!isAuthenticated && (
          <Card className="mb-8 border-0 shadow-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white overflow-hidden" data-testid="guest-dashboard-cta">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
                    <TrendingUp className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-1">
                      Want to See Your Dashboard & Progress Analysis?
                    </h3>
                    <p className="text-indigo-200">
                      Login or register to track your accuracy, identify weak areas, and get personalized insights.
                    </p>
                  </div>
                </div>
                <Button
                  size="lg"
                  onClick={() => navigate("/auth", { state: { from: { pathname: "/dashboard" } } })}
                  className="bg-white text-indigo-700 hover:bg-indigo-50 whitespace-nowrap"
                  data-testid="go-to-dashboard-btn"
                >
                  Go to Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Question Review */}
        <Card data-testid="question-review">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              Question Review
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {results?.map((result, idx) => (
                <div
                  key={result.question_id}
                  className={`border rounded-xl overflow-hidden transition-colors ${
                    result.is_correct ? "border-emerald-200 bg-emerald-50/50" : "border-rose-200 bg-rose-50/50"
                  }`}
                  data-testid={`result-${idx}`}
                >
                  <button
                    onClick={() => toggleQuestion(result.question_id)}
                    className="w-full flex items-center justify-between p-4 text-left"
                  >
                    <div className="flex items-center gap-3">
                      {result.is_correct ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-5 h-5 text-rose-600 flex-shrink-0" />
                      )}
                      <div>
                        <p className="font-medium text-slate-900">Question {idx + 1}</p>
                        <p className="text-sm text-slate-600 line-clamp-1">{result.question_text}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="capitalize text-xs">
                        {result.question_type}
                      </Badge>
                      {expandedQuestions[result.question_id] ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </button>

                  {expandedQuestions[result.question_id] && (
                    <div className="px-4 pb-4 border-t border-slate-200/50 pt-4 animate-fade-in">
                      <p className="font-medium text-slate-900 mb-4">{result.question_text}</p>

                      <div className="space-y-2 mb-4">
                        {result.options.map((option, optIdx) => (
                          <div
                            key={optIdx}
                            className={`p-3 rounded-lg text-sm ${
                              optIdx === result.correct_option
                                ? "bg-emerald-100 text-emerald-800 font-medium"
                                : optIdx === result.user_answer && !result.is_correct
                                ? "bg-rose-100 text-rose-800"
                                : "bg-white text-slate-700"
                            }`}
                          >
                            <span className="font-medium mr-2">{String.fromCharCode(65 + optIdx)}.</span>
                            {option}
                            {optIdx === result.correct_option && (
                              <CheckCircle2 className="w-4 h-4 inline ml-2 text-emerald-600" />
                            )}
                            {optIdx === result.user_answer && !result.is_correct && (
                              <span className="ml-2 text-xs">(Your answer)</span>
                            )}
                          </div>
                        ))}
                      </div>

                      <div className="bg-white rounded-lg p-4 border border-slate-200">
                        <p className="text-sm font-medium text-slate-700 mb-2">Explanation:</p>
                        <p className="text-sm text-slate-600">{result.explanation}</p>

                        {result.why_others_wrong && result.why_others_wrong.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-slate-200">
                            <p className="text-sm font-medium text-slate-700 mb-2">Why other options are wrong:</p>
                            <ul className="space-y-1">
                              {result.why_others_wrong.map(
                                (reason, i) =>
                                  reason && (
                                    <li key={i} className="text-sm text-slate-600">
                                      <span className="font-medium">{String.fromCharCode(65 + i)}:</span> {reason}
                                    </li>
                                  )
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ResultsPage;
